import { useState } from "react";
import { motion } from "framer-motion";
import { Star, MapPin, Search, CalendarDays } from "lucide-react";
import { Button } from "../ui/button";
import { HOTEL } from "@/data/site";

const todayStr = () => new Date().toISOString().split("T")[0];
const tomorrowStr = () => new Date(Date.now() + 86400000).toISOString().split("T")[0];

export const Hero = ({ onSearch }) => {
  const [checkIn, setCheckIn] = useState(todayStr());
  const [checkOut, setCheckOut] = useState(tomorrowStr());
  const [guests, setGuests] = useState(2);

  const submit = () => {
    onSearch({ checkIn, checkOut, guests });
    document.getElementById("rooms")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative min-h-[92vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-[#111827]" />

      <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto w-full py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-sm text-white border border-white/20">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <span className="font-semibold">{HOTEL.rating}</span>
            <span className="text-white/70">({HOTEL.reviews} Google Reviews)</span>
            <span className="text-white/40">·</span>
            <MapPin className="h-3.5 w-3.5" /> <span>Wardha Road, Nagpur</span>
          </div>

          <h1 className="mt-6 font-serif font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Affordable Luxury &amp; Warm Hospitality in Nagpur
          </h1>
          <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
            Comfortable AC rooms from <span className="font-mono font-semibold text-amber-300">₹1,400</span> per night, moments from Wardha Road &amp; Nagpur Airport. Pay at hotel — no advance needed.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 bg-background/95 backdrop-blur rounded-2xl shadow-2xl p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-3 max-w-3xl"
          data-testid="hero-booking-widget"
        >
          <Field label="Check-in" icon={<CalendarDays className="h-4 w-4" />}>
            <input type="date" value={checkIn} min={todayStr()} onChange={(e) => setCheckIn(e.target.value)}
              data-testid="hero-checkin-input" className="w-full bg-transparent text-sm font-medium outline-none" />
          </Field>
          <Field label="Check-out" icon={<CalendarDays className="h-4 w-4" />}>
            <input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)}
              data-testid="hero-checkout-input" className="w-full bg-transparent text-sm font-medium outline-none" />
          </Field>
          <Field label="Guests">
            <select value={guests} onChange={(e) => setGuests(Number(e.target.value))}
              data-testid="hero-guests-select" className="w-full bg-transparent text-sm font-medium outline-none">
              {[1, 2, 3, 4].map((g) => <option key={g} value={g}>{g} Guest{g > 1 ? "s" : ""}</option>)}
            </select>
          </Field>
          <Button onClick={submit} data-testid="hero-search-button"
            className="h-full min-h-[52px] px-6 rounded-xl bg-primary hover:bg-primary/90 gap-2 self-stretch">
            <Search className="h-4 w-4" /> Check Availability
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, icon, children }) => (
  <div className="flex flex-col rounded-xl border border-border bg-card px-3.5 py-2">
    <span className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">{icon}{label}</span>
    {children}
  </div>
);
